import React, { useState, useRef, useCallback, useLayoutEffect, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SiteMobileHeader from '../components/SiteMobileHeader';
import homeHeroLogo from '../assets/Group12.svg';
import MailingListModal from '../components/MailingListModal';
import StickyNavigation from '../components/StickyNavigation';
import SectionContainer from '../components/SectionContainer';
import HomeContactFooter from '../components/HomeContactFooter';
import useReplayOnScrollReveal from '../hooks/useReplayOnScrollReveal';
import useStaggeredRiseOnView, { staggerLineClass } from '../hooks/useStaggeredRiseOnView';
import {
  careersCultureImage,
  careersHeroDesktop,
  careersHeroMobile,
  careersIdeasImage,
  careersImpactImage,
  careersIntroDesktop,
  careersLeadersImage,
  careersWhyWorkDesktop,
} from '../cdn/careersMedia';
import starSvg from '../assets/shared/icons/star.svg';
import { clearContactState, submitMailingListForm } from '../store/slices/contactSlice';
import { validateMailingListForm } from '../utils/mailingListValidation';
import { showToast } from '../utils/toast';
import '../pages/LandingPage.css';
import '../styles/SitePageShell.css';
import './CareersPage.css';

const MAIL_MODAL_CLOSE_MS = 620;
const CAREERS_MAIL = 'careers@neoterra.ae';

const buildCareersMailto = (subject = '') => {
  if (!subject) return `mailto:${CAREERS_MAIL}`;
  return `mailto:${CAREERS_MAIL}?subject=${encodeURIComponent(subject)}`;
};

/** Odd count ≥ 3: extra copies widen scroll runway for seamless infinite x (both directions). */
const CAREER_VALUE_LOOP_COPIES = 15;
const CAREER_VALUE_LOOP_MIDDLE = Math.floor((CAREER_VALUE_LOOP_COPIES - 1) / 2);
const CAREERS_VALUE_AUTOPLAY_MS = 3600;
const CAREERS_GALLERY_AUTOPLAY_MS = 4200;

const CAREER_VALUE_CARDS = [
  {
    id: 'ideas',
    title: 'A place where ideas matter',
    body: 'We value creativity, initiative, and fresh perspectives.',
    /** Mobile: keep on line 2 so line 1 stays longer (see renderCareersValueBody). */
    bodyMobileTail: 'and fresh perspectives.',
    image: careersIdeasImage,
  },
  {
    id: 'leaders',
    title: 'Grow with leaders who inspire',
    body: 'Learn from industry pioneers who believe in mentoring and empowering teams.',
    bodyMobileTail: 'and empowering teams.',
    image: careersLeadersImage,
  },
  {
    id: 'impact',
    title: 'Do work that makes a difference',
    body: 'Your contribution shapes communities and creates lasting value for families across the UAE.',
    bodyMobileTail: 'for families across the UAE.',
    image: careersImpactImage,
  },
  {
    id: 'culture',
    title: 'A culture built on respect & collaboration',
    body: 'We celebrate diverse talents and believe great work happens when people feel supported.',
    bodyMobileTail: 'when people feel supported.',
    image: careersCultureImage,
  },
];

/** Desktop why-gallery 2×2 grid: image/text checkerboard, one card per tile on hover. */
const CAREER_GALLERY_TITLE_LINES = {
  ideas: ['A Place Where Ideas', 'Matter'],
  leaders: ['Grow With Leaders', 'Who Inspire'],
  impact: ['Do Work That Makes', 'A Difference'],
  culture: ['A Culture Built on Respect', '& Collaboration'],
};

const CAREERS_WHY_GALLERY_DESKTOP_TILES = [
  { cardId: 'ideas', variant: 'image', placement: 'ideas' },
  { cardId: 'leaders', variant: 'text', placement: 'leaders' },
  { cardId: 'impact', variant: 'text', placement: 'impact' },
  { cardId: 'culture', variant: 'image', placement: 'culture' },
];

const CAREERS_GALLERY_MOBILE_SLIDES = CAREER_VALUE_CARDS.map((card) => ({
  id: card.id,
  kind: 'tile',
  image: card.image,
  titleLines: CAREER_GALLERY_TITLE_LINES[card.id] ?? [card.title],
  body: card.body,
}));

const CAREER_VALUE_CARDS_PER_LOOP = CAREER_VALUE_CARDS.length;

/** Below `lg`: last phrase stays on one line so the first wrap line carries more copy. */
const renderCareersValueBody = (card) => {
  const { body, bodyMobileTail } = card;
  if (!bodyMobileTail || !body.endsWith(bodyMobileTail)) {
    return body;
  }
  const first = body.slice(0, body.length - bodyMobileTail.length).trimEnd();
  return (
    <>
      <span className="lg:hidden">
        {first}{' '}
        <span className="max-lg:whitespace-nowrap">{bodyMobileTail}</span>
      </span>
      <span className="hidden lg:inline">{body}</span>
    </>
  );
};

const getCarouselSlideStateClass = (slideIndex, centeredIndex) => {
  if (centeredIndex < 0) return ' careers-value-slide--rest';

  const delta = slideIndex - centeredIndex;
  if (delta === 0) return ' careers-value-slide--centered';
  if (delta === -1) return ' careers-value-slide--previous';
  if (delta === 1) return ' careers-value-slide--next';
  if (delta === -2) return ' careers-value-slide--previous-edge';
  if (delta === 2) return ' careers-value-slide--next-edge';

  return ' careers-value-slide--rest';
};

/** Desktop strip: Sobha-style single row with one expanded panel active at a time. */
const CAREER_VALUE_GRID_TOP = [0, 1, 2, 3];
const CAREER_VALUE_GRID_DEFAULT_INDEX = 0;

const CAREERS_JOB_FILTERS = ['All', 'Development', 'Design', 'Sales', 'Finance', 'Marketing', 'Legal'];

const CAREERS_JOB_LISTINGS = [
  {
    id: 'architectural-designer-1',
    title: 'Architectural Designer',
    department: 'Design',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Design',
    status: 'open',
  },
  {
    id: 'interior-design-lead',
    title: 'Interior Design Lead',
    department: 'Design',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Design',
    status: 'closed',
  },
  {
    id: 'senior-project-manager',
    title: 'Senior Project Manager',
    department: 'Development',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Development',
    status: 'open',
  },
  {
    id: 'site-engineer',
    title: 'Site Engineer',
    department: 'Development',
    location: 'Abu Dhabi, UAE',
    type: 'FULL-TIME',
    category: 'Development',
    status: 'open',
  },
  {
    id: 'development-coordinator',
    title: 'Development Coordinator',
    department: 'Development',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Development',
    status: 'closed',
  },
  {
    id: 'sales-executive',
    title: 'Sales Executive',
    department: 'Sales',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Sales',
    status: 'open',
  },
  {
    id: 'senior-sales-manager',
    title: 'Senior Sales Manager',
    department: 'Sales',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Sales',
    status: 'closed',
  },
  {
    id: 'financial-analyst',
    title: 'Financial Analyst',
    department: 'Finance',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Finance',
    status: 'open',
  },
  {
    id: 'accounts-manager',
    title: 'Accounts Manager',
    department: 'Finance',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Finance',
    status: 'open',
  },
  {
    id: 'marketing-manager',
    title: 'Marketing Manager',
    department: 'Marketing',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Marketing',
    status: 'open',
  },
  {
    id: 'content-specialist',
    title: 'Content Specialist',
    department: 'Marketing',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Marketing',
    status: 'closed',
  },
  {
    id: 'legal-counsel',
    title: 'Legal Counsel',
    department: 'Legal',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Legal',
    status: 'open',
  },
  {
    id: 'compliance-officer',
    title: 'Compliance Officer',
    department: 'Legal',
    location: 'Dubai, UAE',
    type: 'FULL-TIME',
    category: 'Legal',
    status: 'closed',
  },
];

/** Values strip: horizontal carousel only below `lg` (1024px); desktop uses the static grid in CSS. */
const CAREERS_VALUE_CAROUSEL_MQ = '(max-width: 1023px)';

const CareersPage = () => {
  const dispatch = useDispatch();
  const contactSubmitting = useSelector((state) => state.contact.loading);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailClosing, setMailClosing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [promotionalConsent, setPromotionalConsent] = useState(false);
  const careersValueCarouselRef = useRef(null);
  const careersValueSegmentRef = useRef(0);
  const careersValueLoopLockRef = useRef(false);
  const careersValueAutoplayRef = useRef(0);
  const careersValueCenteredIndexRef = useRef(-1);
  const careersValueUserScrollingRef = useRef(false);
  const careersValueSettleTimerRef = useRef(0);
  const [careersValueCenteredIndex, setCareersValueCenteredIndex] = useState(-1);
  const [careersValueCarouselMqTick, setCareersValueCarouselMqTick] = useState(0);
  const [galleryMobileIndex, setGalleryMobileIndex] = useState(0);
  const careersHeroRevealRef = useRef(null);
  const careersIntroRevealRef = useRef(null);
  const careersJoinRevealRef = useRef(null);
  const galleryTouchStartRef = useRef({ x: 0, y: 0 });
  const galleryAutoplayRef = useRef(0);
  const galleryAutoplayResumeRef = useRef(0);
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth : 402
  ));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateViewportWidth = () => setViewportWidth(window.innerWidth);
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  const [activeDesktopValueIndex, setActiveDesktopValueIndex] = useState(CAREER_VALUE_GRID_DEFAULT_INDEX);
  const [activeJobFilter, setActiveJobFilter] = useState('All');

  const filteredCareersJobs = useMemo(() => {
    if (activeJobFilter === 'All') return CAREERS_JOB_LISTINGS;
    return CAREERS_JOB_LISTINGS.filter((job) => job.category === activeJobFilter);
  }, [activeJobFilter]);

  const careersDesktopTopRowStyle = useMemo(() => ({
    gridTemplateColumns: CAREER_VALUE_GRID_TOP.map((_, index) => (
      index === activeDesktopValueIndex ? '2.85fr' : '1fr'
    )).join(' '),
  }), [activeDesktopValueIndex]);

  const careersValueSlides = useMemo(() => {
    const out = [];
    for (let copy = 0; copy < CAREER_VALUE_LOOP_COPIES; copy += 1) {
      CAREER_VALUE_CARDS.forEach((card) => {
        out.push({ ...card, instanceIndex: out.length, loopCopy: copy });
      });
    }
    return out;
  }, []);

  const syncCareersValueCarousel = useCallback(() => {
    const track = careersValueCarouselRef.current;
    if (!track || careersValueLoopLockRef.current) return;

    const slides = track.querySelectorAll('.careers-value-slide');
    if (slides.length <= CAREER_VALUE_CARDS_PER_LOOP) return;

    let segment = careersValueSegmentRef.current;
    if (segment <= 0) {
      segment = slides[CAREER_VALUE_CARDS_PER_LOOP].offsetLeft - slides[0].offsetLeft;
      if (segment > 0) careersValueSegmentRef.current = segment;
    }
    if (segment <= 0) return;

    // True infinite wrap: when approaching strip edges, jump to equivalent middle-loop position.
    const left = track.scrollLeft;
    const currentLoop = Math.floor(left / segment);
    const minLoop = 2;
    const maxLoop = CAREER_VALUE_LOOP_COPIES - 3;
    if (currentLoop < minLoop || currentLoop > maxLoop) {
      const loopOffsetWithinSegment = left - (currentLoop * segment);
      const targetLoop = CAREER_VALUE_LOOP_MIDDLE;
      careersValueLoopLockRef.current = true;
      track.scrollLeft = (targetLoop * segment) + loopOffsetWithinSegment;
      window.requestAnimationFrame(() => {
        careersValueLoopLockRef.current = false;
      });
      return;
    }

    const tr = track.getBoundingClientRect();
    const mid = tr.left + tr.width / 2;
    let bestIdx = 0;
    let bestDist = Infinity;
    let currentDist = Infinity;
    const currentIdx = careersValueCenteredIndexRef.current;
    slides.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const d = Math.abs(cx - mid);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
      if (i === currentIdx) currentDist = d;
    });
    const shouldSwitch = currentIdx < 0 || bestIdx === currentIdx || bestDist + 8 < currentDist;
    if (shouldSwitch) {
      careersValueCenteredIndexRef.current = bestIdx;
      setCareersValueCenteredIndex((prev) => (prev !== bestIdx ? bestIdx : prev));
    }
  }, []);

  const stepCareersValueCarousel = useCallback((direction = 1) => {
    const track = careersValueCarouselRef.current;
    if (!track) return;
    const slides = track.querySelectorAll('.careers-value-slide');
    if (slides.length < 2) return;
    const pitch = slides[1].offsetLeft - slides[0].offsetLeft;
    if (pitch <= 0) return;
    track.scrollTo({ left: track.scrollLeft + ((direction >= 0 ? 1 : -1) * pitch), behavior: 'smooth' });
  }, []);

  const settleCareersValueToNearest = useCallback((behavior = 'smooth') => {
    const track = careersValueCarouselRef.current;
    if (!track) return;
    const slides = track.querySelectorAll('.careers-value-slide');
    if (!slides.length) return;
    const tr = track.getBoundingClientRect();
    const mid = tr.left + tr.width / 2;
    let bestIdx = 0;
    let bestDist = Infinity;
    slides.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const d = Math.abs(cx - mid);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    });

    const target = slides[bestIdx];
    if (!target) return;
    const targetLeft = target.offsetLeft - ((track.clientWidth - target.clientWidth) / 2);
    track.scrollTo({ left: targetLeft, behavior });
    careersValueCenteredIndexRef.current = bestIdx;
    setCareersValueCenteredIndex((prev) => (prev !== bestIdx ? bestIdx : prev));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(CAREERS_VALUE_CAROUSEL_MQ);
    const bump = () => setCareersValueCarouselMqTick((n) => n + 1);
    mq.addEventListener('change', bump);
    return () => mq.removeEventListener('change', bump);
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!window.matchMedia(CAREERS_VALUE_CAROUSEL_MQ).matches) return undefined;
    const track = careersValueCarouselRef.current;
    if (!track) return undefined;
    const slides = track.querySelectorAll('.careers-value-slide');
    if (slides.length <= CAREER_VALUE_CARDS_PER_LOOP) return undefined;
    const segment = slides[CAREER_VALUE_CARDS_PER_LOOP].offsetLeft - slides[0].offsetLeft;
    if (segment <= 0) return undefined;
    careersValueSegmentRef.current = segment;
    track.scrollLeft = segment * CAREER_VALUE_LOOP_MIDDLE;
    window.requestAnimationFrame(() => syncCareersValueCarousel());
    return undefined;
  }, [syncCareersValueCarousel, careersValueCarouselMqTick, viewportWidth]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!window.matchMedia(CAREERS_VALUE_CAROUSEL_MQ).matches) return undefined;
    const track = careersValueCarouselRef.current;
    if (!track) return undefined;

    const clearAutoplay = () => {
      if (careersValueAutoplayRef.current) {
        window.clearInterval(careersValueAutoplayRef.current);
        careersValueAutoplayRef.current = 0;
      }
    };

    const startAutoplay = () => {
      clearAutoplay();
      careersValueAutoplayRef.current = window.setInterval(() => {
        stepCareersValueCarousel(1);
      }, CAREERS_VALUE_AUTOPLAY_MS);
    };

    let resumeTimer = 0;
    const pauseAndResume = () => {
      clearAutoplay();
      if (resumeTimer) window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => startAutoplay(), 1200);
    };

    const onWheelControlled = (e) => {
      const primaryDelta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!primaryDelta) return;
      e.preventDefault();
      pauseAndResume();
      // Slow down aggressive wheel/trackpad flicks to avoid carousel break/jump.
      const slowed = Math.max(-72, Math.min(72, primaryDelta * 0.28));
      track.scrollBy({ left: slowed, behavior: 'smooth' });
    };

    startAutoplay();
    track.addEventListener('scroll', pauseAndResume, { passive: true });
    track.addEventListener('touchstart', pauseAndResume, { passive: true });
    track.addEventListener('pointerdown', pauseAndResume);
    track.addEventListener('wheel', onWheelControlled, { passive: false });

    return () => {
      clearAutoplay();
      if (resumeTimer) window.clearTimeout(resumeTimer);
      track.removeEventListener('scroll', pauseAndResume);
      track.removeEventListener('touchstart', pauseAndResume);
      track.removeEventListener('pointerdown', pauseAndResume);
      track.removeEventListener('wheel', onWheelControlled);
    };
  }, [careersValueCarouselMqTick, stepCareersValueCarousel]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!window.matchMedia(CAREERS_VALUE_CAROUSEL_MQ).matches) return undefined;
    const track = careersValueCarouselRef.current;
    if (!track) return undefined;

    let raf = 0;
    const onScroll = () => {
      careersValueUserScrollingRef.current = true;
      if (careersValueSettleTimerRef.current) {
        window.clearTimeout(careersValueSettleTimerRef.current);
      }
      careersValueSettleTimerRef.current = window.setTimeout(() => {
        careersValueUserScrollingRef.current = false;
        settleCareersValueToNearest('smooth');
      }, 120);
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(syncCareersValueCarousel);
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => {
      const slides = track.querySelectorAll('.careers-value-slide');
      if (slides.length > CAREER_VALUE_CARDS_PER_LOOP) {
        const segment = slides[CAREER_VALUE_CARDS_PER_LOOP].offsetLeft - slides[0].offsetLeft;
        if (segment > 0) careersValueSegmentRef.current = segment;
      }
      onScroll();
    });
    ro.observe(track);
    onScroll();

    return () => {
      track.removeEventListener('scroll', onScroll);
      ro.disconnect();
      window.cancelAnimationFrame(raf);
      if (careersValueSettleTimerRef.current) {
        window.clearTimeout(careersValueSettleTimerRef.current);
        careersValueSettleTimerRef.current = 0;
      }
      careersValueUserScrollingRef.current = false;
    };
  }, [syncCareersValueCarousel, careersValueCarouselMqTick, settleCareersValueToNearest]);

  const openMailModal = () => {
    setMailClosing(false);
    setMailOpen(true);
  };

  const scrollToCareersMainContent = () => {
    const target = document.getElementById('careers-gallery');
    if (!target) return;
    const startY = window.scrollY || window.pageYOffset || 0;
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const headerOffset = isDesktop ? 96 : 0;
    const endY = target.getBoundingClientRect().top + startY - headerOffset;
    const distance = endY - startY;
    const durationMs = isDesktop ? 1450 : 320;
    const startTime = performance.now();
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2);
    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / durationMs);
      const eased = easeInOutCubic(progress);
      window.scrollTo(0, startY + distance * eased);
      if (progress < 1) window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  };

  const galleryMobileCount = CAREERS_GALLERY_MOBILE_SLIDES.length;
  const galleryMobilePanelWidthPct = 100 / galleryMobileCount;

  const cycleGalleryMobile = (direction = 1) => {
    const step = direction >= 0 ? 1 : -1;
    setGalleryMobileIndex((i) => (i + step + galleryMobileCount) % galleryMobileCount);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const clearAutoplay = () => {
      if (galleryAutoplayRef.current) {
        window.clearInterval(galleryAutoplayRef.current);
        galleryAutoplayRef.current = 0;
      }
      if (galleryAutoplayResumeRef.current) {
        window.clearTimeout(galleryAutoplayResumeRef.current);
        galleryAutoplayResumeRef.current = 0;
      }
    };

    const startAutoplay = () => {
      clearAutoplay();
      if (window.innerWidth >= 1024) return;
      galleryAutoplayRef.current = window.setInterval(() => {
        setGalleryMobileIndex((i) => (i + 1) % CAREERS_GALLERY_MOBILE_SLIDES.length);
      }, CAREERS_GALLERY_AUTOPLAY_MS);
    };

    startAutoplay();
    window.addEventListener('resize', startAutoplay);
    return () => {
      clearAutoplay();
      window.removeEventListener('resize', startAutoplay);
    };
  }, []);

  const handleGalleryTouchStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    galleryTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
    if (galleryAutoplayRef.current) {
      window.clearInterval(galleryAutoplayRef.current);
      galleryAutoplayRef.current = 0;
    }
    if (galleryAutoplayResumeRef.current) {
      window.clearTimeout(galleryAutoplayResumeRef.current);
      galleryAutoplayResumeRef.current = 0;
    }
  };

  const handleGalleryTouchEnd = (e) => {
    const touch = e.changedTouches?.[0];
    if (!touch) return;
    const deltaX = touch.clientX - galleryTouchStartRef.current.x;
    const deltaY = touch.clientY - galleryTouchStartRef.current.y;
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= 24) {
      cycleGalleryMobile(deltaX < 0 ? 1 : -1);
    }
    if (galleryAutoplayResumeRef.current) window.clearTimeout(galleryAutoplayResumeRef.current);
    galleryAutoplayResumeRef.current = window.setTimeout(() => {
      if (typeof window === 'undefined' || window.innerWidth >= 1024) return;
      if (galleryAutoplayRef.current) window.clearInterval(galleryAutoplayRef.current);
      galleryAutoplayRef.current = window.setInterval(() => {
        setGalleryMobileIndex((i) => (i + 1) % CAREERS_GALLERY_MOBILE_SLIDES.length);
      }, CAREERS_GALLERY_AUTOPLAY_MS);
    }, 1400);
  };

  const closeMailModal = () => {
    if (mailClosing || !mailOpen) return;
    setMailClosing(true);
    window.setTimeout(() => {
      setMailOpen(false);
      setMailClosing(false);
    }, MAIL_MODAL_CLOSE_MS);
  };

  const handleMailSubmit = async (e) => {
    e.preventDefault();
    const validation = validateMailingListForm({ fullName, email, phone });

    if (!validation.isValid) {
      showToast(validation.error, 'error');
      return;
    }

    const { name, email: emailValue, contactNumber } = validation.values;

    try {
      await dispatch(
        submitMailingListForm({
          name,
          email: emailValue,
          contactNumber,
          promotionalConsent,
          sourcePage: 'careers',
        })
      ).unwrap();
      showToast('Thanks! We will keep you informed.', 'success');
      setFullName('');
      setEmail('');
      setPhone('');
      setPromotionalConsent(false);
      closeMailModal();
    } catch (err) {
      showToast(
        typeof err === 'string' ? err : 'Something went wrong. Please try again.',
        'error'
      );
    } finally {
      dispatch(clearContactState());
    }
  };

  useReplayOnScrollReveal('.careers-reveal-on-scroll', 'is-visible', 0.08, '80px 0px -8% 0px', false);

  useStaggeredRiseOnView(careersHeroRevealRef, {
    individual: true,
    durationMs: 1800,
    distancePx: 28,
    threshold: 0.01,
    rootMargin: '0px',
    replay: true,
    reverseOnLeave: true,
    desktopOnly: false,
  });

  useStaggeredRiseOnView(careersIntroRevealRef, {
    individual: true,
    durationMs: 1800,
    distancePx: 28,
    threshold: 0.12,
    rootMargin: '0px 0px -8% 0px',
    replay: true,
    reverseOnLeave: true,
    desktopOnly: false,
  });

  useStaggeredRiseOnView(careersJoinRevealRef, {
    individual: true,
    durationMs: 1800,
    distancePx: 28,
    threshold: 0.08,
    rootMargin: '0px 0px 6% 0px',
    replay: true,
    reverseOnLeave: true,
    desktopOnly: false,
    observeMutations: true,
  });

  return (
    <div className="careers-page site-page-shell relative min-h-screen overflow-x-hidden bg-[#FFFFFF] font-['Poppins',sans-serif] text-[#0a0a0a] md:bg-[#FFF7EA]">
      <SiteMobileHeader
        variant="homeHero"
        logoSrc={homeHeroLogo}
        barClassName="home-top-bar careers-home-top-bar"
        onEnquireClick={openMailModal}
      />

      <div className="relative flex min-h-screen flex-col">
        <div className="careers-viewport-shell">
          <section className="careers-hero-screen" aria-label="Careers">
            <figure className="careers-hero-fig">
              <img
                src={careersHeroMobile}
                alt=""
                className="careers-hero-img careers-hero-img--mobile"
                width="402"
                height="874"
                decoding="async"
                fetchPriority="high"
              />
              <img
                src={careersHeroDesktop}
                alt=""
                className="careers-hero-img careers-hero-img--desktop"
                width="402"
                height="874"
                decoding="async"
                fetchPriority="high"
              />
            </figure>
            <div
              ref={careersHeroRevealRef}
              className="careers-hero-copy careers-hero-copy--desktop"
            >
              <h1 className="careers-hero-desktop__title">
                <span
                  className={staggerLineClass}
                  data-content-line
                  data-reveal-delay="0"
                >
                  Where Talent
                </span>
                <span
                  className={staggerLineClass}
                  data-content-line
                  data-reveal-delay="150"
                >
                  Builds Tomorrow
                </span>
              </h1>
              <p className="careers-hero-desktop__lede">
                <span
                  className={`careers-hero-desktop__lede-line careers-hero-desktop__lede-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="300"
                >
                  where ambition takes shape, create.
                </span>
                <span
                  className={`careers-hero-desktop__lede-line careers-hero-desktop__lede-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="380"
                >
                  grow. lead, your next chapter starts
                </span>
                <span
                  className={`careers-hero-desktop__lede-line careers-hero-desktop__lede-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="460"
                >
                  here
                </span>
                <span
                  className={`careers-hero-desktop__lede-line careers-hero-desktop__lede-line--desktop ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="300"
                >
                  where ambition takes shape, create. grow. lead,
                </span>
                <span
                  className={`careers-hero-desktop__lede-line careers-hero-desktop__lede-line--desktop ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="450"
                >
                  your next chapter starts here
                </span>
              </p>
              <button
                type="button"
                className={`careers-hero-desktop__cta ${staggerLineClass}`}
                data-content-line
                data-reveal-delay="600"
                onClick={scrollToCareersMainContent}
              >
                Learn more
              </button>
            </div>
            <StickyNavigation
              wrapperClassName="home-navigation-sticky careers-navigation-sticky lg:hidden"
              onInquireClick={openMailModal}
            />
          </section>

          <SectionContainer
            ref={careersIntroRevealRef}
            id="careers-after-hero"
            className="careers-intro-wrap"
            maxWidthClass="max-w-none"
          >
            <section className="careers-intro" aria-label="Why people matter at Neoterra">
              <p className="careers-intro-desktop__kicker">
                <span className="careers-intro-desktop__kicker-line" aria-hidden="true" />
                <span
                  className={`careers-intro-desktop__kicker-label ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="0"
                >
                  JOIN NEOTERRA
                </span>
              </p>
              <h2 className="careers-intro-heading">
                <span
                  className={`block lg:hidden ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="120"
                >
                  Where Talent
                </span>
                <span
                  className={`block lg:hidden ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="240"
                >
                  Builds Tomorrow
                </span>
                <span
                  className={`hidden lg:block ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="120"
                >
                  Build the Future
                </span>
                <span
                  className={`hidden lg:block ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="240"
                >
                  with Us
                </span>
              </h2>
              <p className="careers-intro-lead">
                <span
                  className={`careers-intro-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="360"
                >
                  At Neoterra, we are not just developing homes
                </span>
                <span
                  className={`careers-intro-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="400"
                >
                  – we are creating a new way of living.
                </span>
                <span
                  className={`careers-intro-copy--desktop ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="360"
                >
                  At Neoterra, we are not just developing homes – we are creating a new way of living.
                </span>
              </p>
              <p className="careers-intro-follow">
                <span
                  className={`careers-intro-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="480"
                >
                  And none of it is possible without the people
                </span>
                <span
                  className={`careers-intro-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="500"
                >
                  who bring passion, creativity, and commitment
                </span>
                <span
                  className={`careers-intro-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="520"
                >
                  to every detail. If you are someone who
                </span>
                <span
                  className={`careers-intro-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="540"
                >
                  believes in doing things thoughtfully, this is
                </span>
                <span
                  className={`careers-intro-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="560"
                >
                  where you belong.
                </span>
                <span
                  className={`careers-intro-copy--desktop ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="480"
                >
                  And none of it is possible without the people who bring passion, creativity, and commitment to
                  every detail. If you are someone who believes in doing things thoughtfully, this is where you
                  belong.
                </span>
              </p>
            </section>
            <figure
              className={`careers-ref-fig ${staggerLineClass}`}
              data-content-line
              data-reveal-delay="720"
            >
              <img
                src={careersIntroDesktop}
                alt="Neoterra team member reviewing architectural plans"
                className="careers-ref-img careers-ref-img--desktop"
                width={567}
                height={567}
                loading="lazy"
                decoding="async"
              />
            </figure>
          </SectionContainer>

          <SectionContainer
            id="careers-gallery"
            as="section"
            className="careers-why-work careers-reveal-on-scroll"
            aria-label="Why work at Neoterra?"
            maxWidthClass="max-w-none"
          >
            <figure className="careers-why-work-banner relative m-0 hidden h-[130px] w-full min-h-[130px] overflow-hidden bg-[#1a1612] leading-none lg:hidden">
              <figcaption className="absolute inset-0 flex flex-col items-center justify-center gap-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.2)_0%,rgba(0,0,0,0.45)_100%)] px-4 py-3 text-center">
                <h2 className="careers-why-work-heading">
                  <span className="block">Why Work at</span>
                  <span className="block">Neoterra?</span>
                </h2>
              </figcaption>
            </figure>
            <div className="careers-why-gallery">
              <figure className="careers-why-gallery__feature">
                <img
                  src={careersWhyWorkDesktop}
                  alt=""
                  className="careers-why-gallery__img"
                  width={990}
                  height={1080}
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="careers-why-gallery__caption">
                  <h2 className="careers-why-gallery__caption-title">Why Work at Neoterra?</h2>
                </figcaption>
              </figure>
              <div className="careers-why-gallery__right">
                {CAREERS_WHY_GALLERY_DESKTOP_TILES.map(({ cardId, variant, placement }) => {
                  const card = CAREER_VALUE_CARDS.find((item) => item.id === cardId);
                  const titleLines = CAREER_GALLERY_TITLE_LINES[cardId] ?? [card.title];
                  return (
                    <article
                      key={cardId}
                      className={`careers-why-gallery__tile careers-why-gallery__tile--${variant} careers-why-gallery__tile--${placement}`}
                    >
                      <figure className="careers-why-gallery__tile-media">
                        <img
                          src={card.image}
                          alt=""
                          className="careers-why-gallery__img"
                          width={465}
                          height={540}
                          loading="lazy"
                          decoding="async"
                        />
                      </figure>
                      <div className="careers-why-gallery__tile-copy">
                        <p className="careers-why-gallery__tile-title">
                          {titleLines.map((line) => (
                            <span key={line} className="careers-why-gallery__tile-title-line">
                              {line}
                            </span>
                          ))}
                        </p>
                        <p className="careers-why-gallery__tile-body">{card.body}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
            <h2 className="careers-gallery-mobile-heading lg:hidden">
              Why Work at Neoterra?
            </h2>
            <div
              className="careers-gallery-mobile-carousel lg:hidden"
              onTouchStart={handleGalleryTouchStart}
              onTouchEnd={handleGalleryTouchEnd}
            >
              <div className="careers-gallery-mobile-carousel__viewport">
                <div
                  className="careers-gallery-mobile-carousel__track"
                  style={{
                    width: `${galleryMobileCount * 100}%`,
                    transform: `translate3d(-${galleryMobilePanelWidthPct * galleryMobileIndex}%, 0, 0)`,
                  }}
                >
                  {CAREERS_GALLERY_MOBILE_SLIDES.map((slide, slideIndex) => (
                    <article
                      key={slide.id}
                      className={`careers-gallery-mobile-slide careers-gallery-mobile-slide--${slide.kind}${
                        slideIndex === galleryMobileIndex ? ' is-active' : ''
                      }`}
                      style={{ width: `${galleryMobilePanelWidthPct}%` }}
                    >
                      <img
                        src={slide.image}
                        alt=""
                        className="careers-gallery-mobile-slide__img"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="careers-gallery-mobile-slide__copy">
                        <h2 className="careers-gallery-mobile-slide__title">
                          {slide.titleLines.map((line) => (
                            <span key={line} className="careers-gallery-mobile-slide__title-line">{line}</span>
                          ))}
                        </h2>
                        {slide.body ? (
                          <p className="careers-gallery-mobile-slide__body">{slide.body}</p>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
              <div className="careers-gallery-mobile-dots" role="tablist" aria-label="Gallery slides">
                {CAREERS_GALLERY_MOBILE_SLIDES.map((slide, index) => (
                  <button
                    key={slide.id}
                    type="button"
                    role="tab"
                    aria-label={`Go to slide ${index + 1}`}
                    aria-selected={index === galleryMobileIndex}
                    className={`careers-gallery-mobile-dot${index === galleryMobileIndex ? ' is-active' : ''}`}
                    onClick={() => setGalleryMobileIndex(index)}
                  />
                ))}
              </div>
            </div>
          </SectionContainer>

          <section
            className="careers-reveal-on-scroll careers-value-strip hidden"
            aria-label="Life and culture at Neoterra"
          >
            <div className="careers-value-grid-desktop" aria-label="Neoterra workplace values (desktop)">
              <div className="careers-value-grid-desktop__inner">
                <div
                  className="careers-value-grid-desktop__row careers-value-grid-desktop__row--top"
                  style={careersDesktopTopRowStyle}
                  onMouseLeave={() => setActiveDesktopValueIndex(CAREER_VALUE_GRID_DEFAULT_INDEX)}
                  onBlurCapture={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setActiveDesktopValueIndex(CAREER_VALUE_GRID_DEFAULT_INDEX);
                    }
                  }}
                >
                  {CAREER_VALUE_GRID_TOP.map((cardIndex, i) => {
                    const card = CAREER_VALUE_CARDS[cardIndex];
                    const isActive = i === activeDesktopValueIndex;
                    return (
                      <article
                        key={`careers-grid-top-${i}-${card.id}`}
                        className={`careers-value-grid-card careers-value-grid-card--interactive${isActive ? ' careers-value-grid-card--desktop-active' : ''
                          }`}
                        tabIndex={0}
                        onMouseEnter={() => setActiveDesktopValueIndex(i)}
                        onFocus={() => setActiveDesktopValueIndex(i)}
                      >
                        <img
                          src={card.image}
                          alt=""
                          className="careers-value-grid-card__media"
                          width={281}
                          height={326}
                          loading="lazy"
                          decoding="async"
                        />
                        <div
                          className={`careers-value-grid-card__shade${isActive ? ' careers-value-grid-card__shade--featured' : ''
                            }`}
                          aria-hidden="true"
                        />
                        <div className="careers-value-grid-card__desktop-overlay">
                          <div className="careers-value-grid-card__desktop-summary">
                            <h3 className="careers-value-grid-card__title careers-value-grid-card__title--interactive">
                              {card.title}
                            </h3>
                          </div>
                          <div className="careers-value-grid-card__desktop-detail">
                            <h3 className="careers-value-grid-card__title careers-value-grid-card__title--interactive-expanded">
                              {card.title}
                            </h3>
                            <p className="careers-value-grid-card__text careers-value-grid-card__text--interactive">
                              {renderCareersValueBody(card)}
                            </p>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
            <div
              ref={careersValueCarouselRef}
              className="careers-value-carousel careers-value-carousel--mobile-only"
              role="region"
              aria-roledescription="carousel"
              aria-label="Neoterra workplace values"
              tabIndex={0}
            >
              {careersValueSlides.map((card) => (
                <article
                  key={card.instanceIndex}
                  className={`careers-value-slide${getCarouselSlideStateClass(
                    card.instanceIndex,
                    careersValueCenteredIndex
                  )}`}
                >
                  <div className="careers-value-slide__image-wrap">
                    <img
                      src={card.image}
                      alt=""
                      className="careers-value-slide__img"
                      width={281}
                      height={326}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="careers-value-slide__below">
                    <div className="careers-value-slide__connector" aria-hidden="true">
                      <div className="careers-value-slide__lineStack">
                        <span className="careers-value-slide__vertical-line" />
                        <img src={starSvg} alt="" className="careers-value-slide__spark" width="14" height="14" decoding="async" />
                      </div>
                    </div>
                    <div className="careers-value-slide__copy">
                      <h3 className="careers-value-slide__title">{card.title}</h3>
                      <p className="careers-value-slide__text">{renderCareersValueBody(card)}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* Desktop middle sections (Join Our Team + email CTA) temporarily hidden so footer sits below gallery. Do not delete. */}
          {false && (
            <SectionContainer
              id="careers-join-our-team"
              as="section"
              className="careers-open-positions-desktop w-full max-w-full"
              maxWidthClass="max-w-none"
              aria-label="Open positions"
            >
              <div ref={careersJoinRevealRef} className="careers-open-positions-desktop__inner">
                <header className="careers-open-positions-desktop__header">
                  <div className="careers-open-positions-desktop__heading-block">
                    <p className="careers-open-positions-desktop__kicker">
                      <span
                        className={`careers-open-positions-desktop__kicker-reveal ${staggerLineClass}`}
                        data-content-line
                        data-reveal-delay="0"
                      >
                        <span className="careers-open-positions-desktop__kicker-line" aria-hidden="true" />
                        OPEN POSITIONS
                      </span>
                    </p>
                    <h2
                      className={`careers-open-positions-desktop__title ${staggerLineClass}`}
                      data-content-line
                      data-reveal-delay="150"
                    >
                      Join Our Team
                    </h2>
                  </div>
                  <p className="careers-open-positions-desktop__lede">
                    <span
                      className={`careers-open-positions-desktop__lede-line ${staggerLineClass}`}
                      data-content-line
                      data-reveal-delay="300"
                    >
                      We are always looking for exceptional talent.
                    </span>
                    <span
                      className={`careers-open-positions-desktop__lede-line ${staggerLineClass}`}
                      data-content-line
                      data-reveal-delay="450"
                    >
                      If you don&apos;t see a role that fits, send your CV
                    </span>
                    <span
                      className={`careers-open-positions-desktop__lede-line ${staggerLineClass}`}
                      data-content-line
                      data-reveal-delay="600"
                    >
                      to{' '}
                      <a
                        href={buildCareersMailto()}
                        className="careers-open-positions-desktop__email"
                      >
                        {CAREERS_MAIL}
                      </a>
                    </span>
                  </p>
                </header>

                <div
                  className={`careers-open-positions-desktop__filters ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="750"
                  role="tablist"
                  aria-label="Filter open positions by department"
                >
                  {CAREERS_JOB_FILTERS.map((filter) => {
                    const isActive = activeJobFilter === filter;
                    return (
                      <button
                        key={filter}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`careers-open-positions-desktop__filter${isActive ? ' careers-open-positions-desktop__filter--active' : ''
                          }`}
                        onClick={() => setActiveJobFilter(filter)}
                      >
                        {filter}
                      </button>
                    );
                  })}
                </div>

                <div className="careers-open-positions-desktop__list" role="list">
                  {filteredCareersJobs.map((job, index) => {
                    const isClosed = job.status === 'closed';
                    return (
                      <article
                        key={job.id}
                        className={`careers-open-positions-desktop__row ${staggerLineClass}`}
                        data-content-line
                        data-reveal-delay={String(780 + index * 50)}
                        role="listitem"
                      >
                        <div className="careers-open-positions-desktop__role">
                          <h3 className="careers-open-positions-desktop__role-title">{job.title}</h3>
                          <p className="careers-open-positions-desktop__role-meta">
                            <span>{job.department}</span>
                            <span className="careers-open-positions-desktop__role-meta-dot" aria-hidden="true" />
                            <span>{job.location}</span>
                          </p>
                        </div>
                        <span className="careers-open-positions-desktop__type">{job.type}</span>
                        {isClosed ? (
                          <span className="careers-open-positions-desktop__action careers-open-positions-desktop__action--closed">
                            Closed
                          </span>
                        ) : (
                          <a
                            href={buildCareersMailto(`Application: ${job.title}`)}
                            className="careers-open-positions-desktop__action careers-open-positions-desktop__action--apply"
                          >
                            Apply Now
                          </a>
                        )}
                      </article>
                    );
                  })}
                </div>
              </div>
            </SectionContainer>
          )}

          <SectionContainer
            as="section"
            className="careers-open-roles-mobile careers-reveal-on-scroll mt-4 hidden w-full max-w-full"
            maxWidthClass="max-w-none"
            aria-label="Open roles"
          >
            <figure className="careers-open-roles-banner relative m-0 w-full max-w-none overflow-hidden bg-[#1a1612] leading-none">
              <figcaption className="careers-open-roles-banner__caption absolute inset-0 flex flex-col items-center justify-center bg-[linear-gradient(180deg,rgba(0,0,0,0.2)_0%,rgba(0,0,0,0.45)_100%)] px-4 py-3 text-center">
                <h2 className="careers-open-roles-heading">OPEN ROLES</h2>
              </figcaption>
            </figure>
          </SectionContainer>

          <SectionContainer
            as="section"
            className="careers-no-open-positions-band careers-no-open-positions-mobile careers-reveal-on-scroll hidden w-full max-w-full"
            maxWidthClass="max-w-none"
            aria-label="Open positions status"
          >
            <div className="mx-auto flex w-full max-w-[402px] items-center justify-center bg-[#FCFBF7] px-[25.5px] pb-8 pt-7">
              <div className="flex min-h-[98px] w-full max-w-[351px] items-center justify-center rounded-[14.76px] border-[1.24px] border-[#0A0A0A0D] bg-white px-[26.54px] py-5 shadow-[0px_1.05px_2.11px_-1.05px_#0000001A,0px_1.05px_3.16px_0px_#0000001A]">
                <p className="m-0 w-full text-center font-['Poppins',sans-serif] text-[16px] font-light leading-[21.09px] tracking-[0] text-[#0a0a0a]">
                  No open position
                </p>
              </div>
            </div>
          </SectionContainer>

          {/* Desktop email CTA temporarily hidden with Join Our Team. Do not delete. */}
          {false && (
            <SectionContainer
              as="section"
              className="careers-email-cta-desktop careers-reveal-on-scroll w-full max-w-full"
              maxWidthClass="max-w-none"
              aria-label="Send your portfolio or CV"
            >
              <div className="careers-email-cta-desktop__inner">
                <div className="careers-email-cta-desktop__copy">
                  <h2 className="careers-email-cta-desktop__title">Don&apos;t see the right role?</h2>
                  <p className="careers-email-cta-desktop__text">
                    <span className="careers-email-cta-desktop__text-line careers-email-cta-desktop__text-line--lead">
                      We&apos;re always growing. Send us your portfolio or CV and we&apos;ll
                    </span>
                    <span className="careers-email-cta-desktop__text-line">
                      keep you in mind for future opportunities.
                    </span>
                  </p>
                </div>
                <a
                  href={buildCareersMailto('Portfolio / CV submission')}
                  className="careers-email-cta-desktop__button"
                >
                  Get in Touch
                </a>
              </div>
            </SectionContainer>
          )}

          <SectionContainer
            as="section"
            className="careers-cta-mobile careers-reveal-on-scroll hidden w-full max-w-full"
            maxWidthClass="max-w-none"
            aria-label="Careers contact call to action"
          >
            <figure className="careers-cta-card-figure bg-[#1a1612]">
              <figcaption className="careers-cta-card-caption absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.28)_0%,rgba(0,0,0,0.58)_100%)] px-5 py-4">
                <h2 className="careers-cta-card-title">
                  <span className="inline lg:hidden">Don&apos;t see a role that suits you?</span>
                  <span className="hidden lg:inline">
                    Don&apos;t see a role that
                    <br />
                    suits you?
                  </span>
                </h2>
                <p className="careers-cta-card-lead">Send us your CV at</p>
                <a href="mailto:careers@neoterra.ae" className="careers-cta-card-email">
                  careers@neoterra.ae
                </a>
              </figcaption>
            </figure>
          </SectionContainer>

          <div className="careers-reveal-on-scroll careers-mobile-footer-gap lg:hidden">
            <HomeContactFooter />
          </div>

          <h1 className="careers-sr-only">Careers at Neoterra Developments</h1>
        </div>
      </div>

      <div className="site-page-footer-wrap relative z-[30] isolate hidden bg-[#162D24] lg:block">
        <HomeContactFooter variant="home" sectionId="careers-contact" />
      </div>

      <MailingListModal
        open={mailOpen}
        closing={mailClosing}
        onClose={closeMailModal}
        onSubmit={handleMailSubmit}
        submitting={contactSubmitting}
        fullName={fullName}
        setFullName={setFullName}
        email={email}
        setEmail={setEmail}
        phone={phone}
        setPhone={setPhone}
        promotionalConsent={promotionalConsent}
        setPromotionalConsent={setPromotionalConsent}
        idPrefix="careers-mail"
      />
    </div>
  );
};

export default CareersPage;
